/**
 * 動画メタ（詳細）の文字起こしの行。**再生中の行を目で追える**ようにする。
 *
 * ## いま話している行を出す
 *
 * プレイヤーの `timeupdate` で決まった行（`activeIndex`）に印を付け、
 * `aria-current` を立てる。**再生していない動画では何も付けない**
 * （原本の無い動画で、どこかの行が「いま」に見えないようにする）。
 *
 * ## 枠の外へ出たら追いかける。ただし読んでいる人の邪魔はしない
 *
 * 印の付いた行が枠の表示域から外れたら、枠の中でその行まで送る。
 * 枠を手で動かした直後（2 秒以内）は追いかけない。前の行を読み返している
 * 最中に引き戻されると読めなくなるためである。
 */

import { useEffect, useRef } from "react";

import { formatClock } from "./VideoSegmentList";

/** 手で動かしたあと、追いかけるのを控える時間。 */
const MANUAL_SCROLL_GRACE_MS = 2000;

/** 自分で送ったときの scroll が届くまでの猶予。 */
const PROGRAMMATIC_SCROLL_MS = 300;

export interface TranscriptLineView {
  readonly index: number;
  readonly startSec: number;
  readonly endSec: number;
  readonly text: string;
}

/**
 * 再生位置にあたる行。**無ければ null**（行と行の間の無音もある）。
 */
export function activeTranscriptIndexAt(
  lines: readonly TranscriptLineView[],
  current: number,
): number | null {
  return (
    lines.find((line) => current >= line.startSec && current < line.endSec)
      ?.index ?? null
  );
}

/** 行が枠の表示域に収まっているか。大きさが測れないときは収まっているとみなす。 */
function isVisibleWithin(line: HTMLElement, frame: HTMLElement): boolean {
  const lineRect = line.getBoundingClientRect();
  const frameRect = frame.getBoundingClientRect();
  if (frameRect.height === 0) return true;
  return lineRect.top >= frameRect.top && lineRect.bottom <= frameRect.bottom;
}

export function VideoTranscriptList({
  lines,
  activeIndex,
  onSeek,
}: {
  lines: readonly TranscriptLineView[];
  /** 再生位置にあたる行。再生していなければ null */
  activeIndex: number | null;
  /** 行の時刻から再生する。原本が無ければ渡さない（押せない形にする） */
  onSeek?: (seconds: number) => void;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const lastManualScroll = useRef<number>(Number.NEGATIVE_INFINITY);
  /** 自分で送った直後の scroll を、手で動かしたことに数えないための期限。 */
  const programmaticUntil = useRef(0);

  useEffect(() => {
    if (activeIndex === null) return;
    const frame = frameRef.current;
    if (frame === null) return;
    if (Date.now() - lastManualScroll.current < MANUAL_SCROLL_GRACE_MS) return;
    const line = frame.querySelector<HTMLElement>(
      `[data-transcript-index="${String(activeIndex)}"]`,
    );
    if (line === null || isVisibleWithin(line, frame)) return;
    // jsdom など、scrollIntoView を持たない環境では何もしない
    if (typeof line.scrollIntoView !== "function") return;
    programmaticUntil.current = Date.now() + PROGRAMMATIC_SCROLL_MS;
    line.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  return (
    <div
      ref={frameRef}
      aria-label="文字起こし"
      className="vd-video-transcripts"
      onScroll={() => {
        // 自分で送ったぶんは、手で動かしたことに数えない
        if (Date.now() < programmaticUntil.current) return;
        lastManualScroll.current = Date.now();
      }}
    >
      {lines.map((line) => {
        const active = activeIndex === line.index;
        return (
          <div
            key={line.index}
            data-transcript-index={line.index}
            className={`vd-video-transcript${active ? " vd-video-transcript--active" : ""}`}
            aria-current={active ? "true" : undefined}
          >
            <button
              type="button"
              disabled={onSeek === undefined}
              onClick={() => onSeek?.(line.startSec)}
              aria-label={`${formatClock(line.startSec)} から再生`}
            >
              {formatClock(line.startSec)}
            </button>
            <span>{line.text}</span>
          </div>
        );
      })}
    </div>
  );
}
